import {
  DEFAULT_ACTIVE_OFFSET_PX,
  useActiveSection,
} from "@hooks/V2/useActiveSection"
import { useCallback, useEffect, useState } from "react"

// Once a tab is clicked, keep it pinned as "active" until the resulting
// smooth-scroll gesture actually settles (no `scroll` events for this long).
// Safe to do because the active section itself is now derived continuously
// from real scroll position (see useActiveSection) rather than from
// edge-triggered viewport-enter events, so whatever it settles on is correct.
const SCROLL_SETTLE_DELAY_MS = 150

export { DEFAULT_ACTIVE_OFFSET_PX }

type ScrollSpyTabDef<TabKey extends string, SectionKey extends string> = {
  key: TabKey
  sectionKeys: SectionKey[]
}

export const useScrollSpyTabs = <
  TabKey extends string,
  SectionKey extends string,
>(
  tabDefs: ScrollSpyTabDef<TabKey, SectionKey>[],
  sectionKeys: SectionKey[],
  activeOffsetPx: number = DEFAULT_ACTIVE_OFFSET_PX
) => {
  const [pendingTabKey, setPendingTabKey] = useState<TabKey | null>(null)

  const {
    activeSectionKey,
    registerSectionRef,
    getSectionElement,
    registerScrollRoot: scrollContainerRef,
    getScrollRootElement,
  } = useActiveSection(sectionKeys, {
    trackScrollRoot: true,
    offsetPx: activeOffsetPx,
  })

  const currentSectionKey = activeSectionKey ?? sectionKeys[0]
  const activeTab = tabDefs.find(
    (def) => currentSectionKey && def.sectionKeys.includes(currentSectionKey)
  )
  const activeTabKey = pendingTabKey ?? activeTab?.key

  useEffect(() => {
    if (!pendingTabKey) return

    const scrollContainer = getScrollRootElement()
    if (!scrollContainer) return

    let settleTimeoutId: ReturnType<typeof setTimeout>

    const clearPendingTabKey = () => setPendingTabKey(null)

    const handleScroll = () => {
      clearTimeout(settleTimeoutId)
      settleTimeoutId = setTimeout(clearPendingTabKey, SCROLL_SETTLE_DELAY_MS)
    }

    handleScroll()
    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    })

    return () => {
      clearTimeout(settleTimeoutId)
      scrollContainer.removeEventListener("scroll", handleScroll)
    }
  }, [pendingTabKey, getScrollRootElement])

  const scrollToSection = useCallback(
    (tabKey: TabKey, sectionKey: SectionKey) => {
      setPendingTabKey(tabKey)
      getSectionElement(sectionKey)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    },
    [getSectionElement]
  )

  return {
    scrollContainerRef,
    activeTabKey,
    scrollToSection,
    registerSectionRef,
  }
}
